import {
  membersApi,
  useMemberGetQuery,
  useMemberInfinitePostQuery,
  useMemberMutate,
  useMemberPostQuery,
  API_BASE,
  getSubdomain,
} from "modules/members/api";
import { useMemberAuthStore } from "modules/members/auth";
import { memberErrorMessage } from "./memberErrors";
import { useEffect, useMemo } from "react";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@chakra-ui/react";
import { MFApp } from "./apps";
import type { AppTenantFields } from "modules/apps/tenants";
import type { MFAccessKind, MFAccessPath } from "state/accessPaths";
import { MFMember, MFMemberAccount } from "./member";

// Legacy access review types
export interface MFAccessReview {
  id: string;
  status: "in_progress" | "ready_for_approval";
  startedAt: string;
  assingeeId: string;
  insertedAt: string;
  assignedBy?: MFMember;
  app: MFApp;
}

export interface MFAccessReviewWithTotals {
  id: string;
  required: number;
  notRequired: number;
  outOfScope: number;
  removed: number;
  requiringRemoval: number;
  total: number;
  review: MFAccessReview;
  roles: string[];
  missingRoles: number;
}

export interface MFAccessReviewAccount {
  id: string;
  status:
    | "complete"
    | "requires_review"
    | "requires_removal"
    | "already_removed";
  review: string;
  role: string[];
  outOfScopeReason: string | null;
  assignedGroups?: string[];
  memberAccount: MFMemberAccount;
  accessReviewId: string;
  lastDetectedUsage: string | null;
  note?: string;
  lastReview?: string;
}

export type MFAccountAccessReviewFilters = {
  access_review_id?: string;
  search?: string;
  has_review?: boolean;
  missing_role?: boolean;
  department?: string[];
  job_title?: string[];
  role?: string[];
  last_review?: string[];
};

// Campaign types
export interface MFCampaign {
  id: string;
  name: string;
  description: string;
  dueDate: string;
  status: "draft" | "in_progress" | "completed";
  createdBy: string;
  insertedAt: string;
  notificationEnabled: boolean;
  outcomeOptions: Array<{
    name: string;
    standard_mapping: string;
    requires_note?: boolean;
    // Configured remediation the backend executes the moment this outcome is
    // recorded (e.g. `{ type: "deprovision" }`). Omitted when the outcome has
    // no actions.
    actions?: Array<{ type?: string | null } | null> | null;
  }>;
  // Per-application reviewer instructions (PLO-4817), keyed by resource so
  // the resource drawer can surface each review's instruction. Older API
  // responses may omit this.
  applicationInstructions?: Array<{
    resourceId: string;
    text: string;
  }> | null;
  // True when this member has a 2nd-review (stage > 1) set in this cycle
  // (PLO-4806) — drives the campaigns-list "2nd review" badge. Older API
  // responses omit it (treated as false).
  hasSecondReview?: boolean;
  // Owner sign-off cycles (PLO-5185) sit in the same campaign list as standard
  // ones; this is what tells them apart. Older API responses omit it (treated
  // as "standard"). `MFReviewType` is declared with the attestation types
  // further down this file.
  reviewType?: MFReviewType;
}

export interface MFCampaignDetails {
  campaign: MFCampaign;
  reviews: MFCampaignReview[];
  stats: {
    total_reviews: number;
    completed_reviews: number;
    accounts: {
      total: number;
      completed: number;
      requires_review: number;
      requires_removal: number;
      // Answered IAM queries awaiting the reviewer's re-decision (PLO-4816).
      answered_queries?: number;
    };
    completion_percentage: number;
    // Campaign-level rollup of answered queries needing action — surfaced on
    // the portal Home "queries answered — action needed" strip (PLO-4816).
    answered_queries?: number;
  };
}

export interface MFCampaignReview {
  id: string;
  status: "in_progress" | "ready_for_approval" | "complete";
  startedAt: string;
  name: string;
  hasRevisionRequested?: boolean;
  // Owner sign-off reviews (PLO-5185): the member's attestation for this
  // system, so the row can link straight to /members/attestations/:id and show
  // its sign-off status. Both null on a standard reviewer review.
  attestationId?: string | null;
  attestationStatus?: MFAttestationStatus | null;
  stats: {
    total: number;
    completed: number;
    requires_review: number;
    requires_removal: number;
  };
  resource: AppTenantFields & {
    id: string;
    name: string;
    type: string;
    imageUrl?: string;
    // The resource's own image, or a fallback (app logo / integration brand
    // logo) when it has none of its own — e.g. a Slack channel shows the
    // Slack logo instead of the generic resource-type glyph. Prefer this over
    // imageUrl when rendering an icon.
    logoUrl?: string;
    extId?: string;
    // App permission risk score (0–100). Banded low/medium/high on the FE for
    // the reviewer-side risk filter (PLO-4816). Null when the resource has no
    // scored app permission.
    appPermissionRiskScore?: number | null;
  };
}

// Deadline-extension rules + state for a reviewer's set (PLO-4856). Present on
// the review-details payload only when the cycle has extension rules enabled
// and the viewer is the assigned reviewer (never an attestor); `null`
// otherwise. `effectiveDueDate` is the REAL deadline to display (override ||
// cycle due date). All date fields are ISO strings.
export interface MFDeadlineExtension {
  enabled: boolean;
  // Requests <= this auto-grant instantly; null/0 = every request queues for
  // an admin decision.
  autoGrantMaxDays: number | null;
  // Non-denied requests allowed for this set (a denial frees its slot).
  maxRequestsPerSet: number;
  // Cap on cumulative extension days from the ORIGINAL cycle due date.
  maxTotalExtensionDays: number;
  requireReason: boolean;
  // How many more requests this reviewer may still raise.
  remainingRequests: number;
  // How many more days can still be granted in total.
  remainingTotalDays: number;
  // The REAL deadline to display: override || cycle due date.
  effectiveDueDate: string | null;
  // Set once an extension has been granted (the new due date).
  dueDateOverride: string | null;
  pendingRequest: {
    id: string;
    requestedDays: number;
    reason: string | null;
    insertedAt: string;
  } | null;
  // Reason of the most recent DENIED request, if any (and no pending).
  deniedReason: string | null;
}

// A review-scope (account-less) query the reviewer raises to the campaign admin
// (PLO-4803). Unlike the per-account query lifecycle, this asks about the review
// as a whole; the `note` is the question. `resolvedAt` null = still OPEN
// (awaiting the admin) — an open general query blocks submitting the review for
// approval (enforced server-side). Once answered, `adminResponse` + `resolvedAt`
// (+ `answeredBy`) are populated. All date fields are ISO strings.
export interface MFGeneralQuery {
  id: string;
  note: string;
  raisedAt: string | null;
  adminResponse: string | null;
  resolvedAt: string | null;
  answeredBy: string | null;
}

export interface MFUndecidedRowsRouting {
  nextStage: number;
  reviewerAssignmentName: string | null;
}

export type MFCycleStatus = "draft" | "in_progress" | "completed" | "archived";

export interface MFReviewDetails {
  review: {
    id: string;
    status: "in_progress" | "ready_for_approval" | "complete";
    startedAt: string;
    name: string;
    resource: AppTenantFields & {
      id: string;
      name: string;
      type: string;
      // PLO-5211: the resource's own description, shown under the name in the
      // review header. Absent on older payloads and when none is set.
      description?: string | null;
      imageUrl?: string;
      // See MFCampaignReview.resource.logoUrl — prefer this for icon rendering.
      logoUrl?: string;
      owners?: Array<{
        id: string;
        type: string;
        memberId?: string | null;
      }>;
    };
    campaignId?: string;  // Legacy field
    campaignCycleId?: string;  // New field name
    isResourceOwner?: boolean;  // Indicates if the member is the resource owner
    // Admin-authored instruction for THIS review's resource (PLO-4817), or
    // null when the campaign has none for it. Older API responses omit it.
    applicationInstruction?: string | null;
  };
  stats: {
    total: number;
    completed: number;
    requires_review: number;
    requires_removal: number;
  };
  accountSetId: string | null;
  accountSetStatus: "in_progress" | "submitted" | "revision_requested" | "approved" | null;
  accountSetRevisionMessage: string | null;
  // Which stage this reviewer's set belongs to, and a convenience flag for the
  // "2nd review" header badge (PLO-4806). Older API responses omit these
  // (stage 1 / not a second review).
  accountSetStage?: number;
  isSecondReview?: boolean;
  // Total configured stages (N) for the "Re-review — stage X of N" banner
  // (PLO-4806). Defaults to 1 (single-pass) on older payloads.
  stageCount?: number;
  // Whether this reviewer may submit with accounts left undecided; the blank
  // rows go on to the next stage's undecided-rows reviewer as open rows. True
  // only for a reviewer on a non-final stage with the campaign setting on.
  // Older API responses omit these (treated as false / final stage / null).
  allowUndecidedSubmission?: boolean;
  isFinalStage?: boolean;
  // Where the undecided accounts go. Non-null exactly when
  // `allowUndecidedSubmission` is true; `reviewerAssignmentName` is null when
  // the next stage's reviewer can't be resolved.
  undecidedRowsRouting?: MFUndecidedRowsRouting | null;
  // `"reviewer"` (assigned to a set, can edit) or `"attestor"` (assigned
  // to an attestation row, read-only drill-in from the attestation page).
  // Older API responses may omit this; treat that as reviewer-mode.
  viewMode?: "reviewer" | "attestor" | null;
  // The review cycle's status. Only `"in_progress"` takes decisions: once the
  // admin ends (`"completed"`) or archives it, the server refuses every
  // reviewer write and the page locks its controls. `null` for a legacy review
  // with no cycle; older API responses omit it (both treated as open).
  cycleStatus?: MFCycleStatus | null;
  // Server-computed completion ratio for this review (0-100), rounded to one
  // decimal, so 1999 of 2000 decided reads as 100.0. Nothing on the review page
  // reads it for that reason (PLO-6038): the meter, the workflow state and the
  // submit gate all count `stats` rows. Kept because the API sends it.
  completionPercentage?: number;
  // Cycle-level attestation snapshot. `null` for legacy reviews with no cycle.
  // When `attestationMode === "reviewer_per_set"`, the portal collects the
  // reviewer's confirmations inline at submit time and creates the attestation
  // alongside the account set submission.
  cycle: {
    attestationMode: MFAttestationMode | null;
    statements: Array<{
      id: string;
      text: string;
      required: boolean;
      order: number;
    }>;
    // True when the cycle only RECORDS decisions and never deprovisions
    // (PLO-5083). The reviewer UI keys its "will deprovision immediately"
    // warning, deprovision-consent gate, and irreversible row-lock off this so
    // a record-decisions-only review stops claiming access will be removed.
    // Absent on older API responses (treated as false).
    decisionOnly?: boolean;
    // The campaign's "Allow users to correct data" setting, snapshotted onto
    // the cycle (PLO-5336). False hides the "Fix incorrect data" actions; the
    // server rejects the calls either way. Absent on older API responses and on
    // legacy cycle-less reviews (both treated as true).
    allowCorrections?: boolean;
    // The cycle's review type (PLO-5731). `entitlement_only` means every row
    // is one entitlement: the reviewer table drops the flag-for-adjustment
    // action, and a removal decision is scoped to that entitlement alone.
    // Absent on older API responses (treated as "standard").
    reviewType?: MFReviewType;
    // One review for the campaign's whole scope: each row names the resource
    // it reviews, so the table shows a Resource column and groups by person.
    // Absent on older API responses (treated as false).
    combineReviews?: boolean;
  } | null;
  // Review deadline-extension rules + state (PLO-4856). `null` when the cycle
  // has no rules, rules are disabled, or the viewer is an attestor. Older API
  // responses omit it (treated as null).
  deadlineExtension?: MFDeadlineExtension | null;
  // Review-scope (account-less) queries raised to the campaign admin (PLO-4803).
  // Open ones (`resolvedAt` null) block submitting the review; answered ones
  // surface in the shared "View responses" modal. Older API responses omit this
  // (treated as an empty list).
  generalQueries?: MFGeneralQuery[] | null;
}

// Entitlement snapshot for access review accounts
// Peer-comparison signal for a single entitlement (PLO-4815): how many of the
// holder's same-department peers in this review also hold it.
export interface MFPeerComparison {
  holders: number;
  peerGroupSize: number;
}

export interface MFAccessReviewAccountEntitlement {
  id: string;
  snapshotEntitlementId: string | null;
  snapshotEntitlementName: string;
  snapshotEntitlementType: string | null;
  // PLO-5211: what holding this entitlement lets a person do, snapshotted
  // at review creation. Null/absent on rows created before the column and
  // when the entitlement had no description.
  snapshotEntitlementDescription?: string | null;
  flaggedForAdjustment: boolean;
  flaggedNote: string | null;
  // "X of Y peers hold this". Null when no comparable peer group exists.
  peerComparison?: MFPeerComparison | null;
}

// One applied "Fix incorrect data" correction (PLO-4803), from the account row's
// server-persisted jsonb list. Entitlement NAMES added/removed, the applier's
// display name, and when it was applied.
export interface MFEntitlementCorrection {
  added: string[];
  removed: string[];
  correctedBy: string;
  at: string;
}

export interface MFReviewAccount {
  id: string;
  status:
    | "requires_review"
    | "requires_removal"
    | "complete"
    | "already_removed"
    | "removed";
  review?: "required" | "not_required" | "out_of_scope" | "query" | null;
  outOfScopeReason?: string | null;
  note?: string | null;
  // Query lifecycle (PLO-4816, reviewer side). The reviewer raises a query to
  // the IAM team (`review === "query"`, the question lives in `note`);
  // `queriedAt` stamps when. The IAM admin answers with `adminResponse` and
  // `queryResolvedAt`, and the account returns to `requires_review` so the
  // reviewer can re-decide informed. All null when no query was ever raised.
  queriedAt?: string | null;
  adminResponse?: string | null;
  queryResolvedAt?: string | null;
  // The IAM admin who answered the query (display name). Absent on older
  // payloads that predate this field — treat as "unknown responder".
  answeredBy?: string | null;
  // The removal (or other remediation) this row's decision started (PLO-5749).
  // A row locks only once its removal is `complete`; one still pending is
  // called off by the server when the reviewer re-decides. Absent on older
  // payloads, which keep the config-based lock.
  remediationType?:
    | "deprovision"
    | "adjust_entitlements"
    | "remove_indirect_access"
    | null;
  remediationStatus?: "in_progress" | "complete" | "failed" | null;
  // No `role` / `accessLevel`. The server stopped sending both: they were the
  // same entitlement names as `entitlements` below, just flattened, and
  // entitlements are the model on access reviews now.
  // Stage-1 decision context (PLO-4806). Populated on 2nd-review rows so the
  // portal can show "kept by <name> on <date>" as a compact hint + in the
  // drawer. All null/absent on stage-1 rows.
  stage1Review?: string | null;
  stage1Note?: string | null;
  stage1ReviewedAt?: string | null;
  stage1ReviewedByName?: string | null;
  // The row's own review stage, and the stage-1 account it was spawned from
  // (PLO-4806). On a 2nd-stage reviewer's list the same person can appear as
  // BOTH their decided stage-1 row and the fresh stage-2 row; `originAccountId`
  // (the stage-1 account id, null on stage-1 rows) links the two so the stage-1
  // predecessor can be folded away instead of shown as a duplicate. `stage`
  // defaults to 1 on older payloads / single-pass reviews.
  stage?: number;
  originAccountId?: string | null;
  // Entitlements snapshot data
  entitlements?: MFAccessReviewAccountEntitlement[];
  // The ONE entitlement this row reviews, in an entitlement-only cycle
  // (PLO-5731). Null/absent means the row covers the whole account, which is
  // every row of a standard cycle. When set, `entitlements` holds exactly that
  // one snapshot, and a removal decision takes away only it.
  entitlementId?: string | null;
  entitlementCount?: number;
  flaggedCount?: number;
  // Applied entitlement corrections (PLO-4803), persisted server-side as an
  // account-row jsonb list. Each entry records the entitlement names added /
  // removed by a "Fix incorrect data" correction, who applied it (display name)
  // and when. Absent/empty on older payloads or accounts never corrected.
  entitlementCorrections?: MFEntitlementCorrection[];
  reviewedAt?: string | null;
  resourceAccessId: string;
  resource: AppTenantFields & {
    id: string;
    name: string;
    type: string;
    // The resource's own identifier at its source, its logo, and the
    // integration it comes from (the logo's fallback). Absent on older payloads.
    extId?: string | null;
    logoUrl?: string | null;
    integration?: string | null;
  };
  accessGrantedAt?: string;
  accessExpiresAt?: string;
  isIndefinite?: boolean;
  lastUsageDate?: string | { date: string | null };
  // Snapshot fields captured at review creation (PLO-4815). Stable last-activity
  // + peer context even as the live member record drifts.
  snapshotLastAccessedAt?: string | { date: string | null } | null;
  snapshotDepartment?: string | null;
  snapshotJobTitle?: string | null;
  // Account-level peer summary: the peer-group label + size, so the row/drawer
  // can render "N peers in <dept>" and apply a display floor.
  peerComparison?: {
    groupKey: string | null;
    peerGroupSize: number;
  } | null;
  identity?: {
    id: string;
    // Surfaced for service-account rows where the member fields are all
    // null — gives the FE something readable to render.
    extId?: string | null;
    displayName?: string | null;
    // The identity's login name (e.g. "juliet.taylor"). Preferred over extId
    // when labelling the identity: two identities of the same person share a
    // display name but differ by username (PLO-5750).
    username?: string | null;
    type?: string | null;
    auth_type: string;
    mfa_enabled: boolean;
  };
  // Service accounts have no associated Member; every field except id may
  // be null/empty after the BE left-join. `id` is also null for SA rows.
  member: {
    id: string | null;
    name: string | null;
    status: string | null;
    email: string | null;
    department?: string | null;
    jobTitle?: string | null;
    location?: string | null;
    managerId?: string | null;
    profilePictureUrl?: string | null;
  };
  // Direct = the identity's own edge on the reviewed resource; indirect = a
  // chain through one or more intermediaries (PLO-5189). Absent on rows created
  // before the column, which read as direct.
  accessKind?: MFAccessKind;
  // Every confirmed route that backed the row at review creation. Null on
  // direct rows.
  accessPaths?: MFAccessPath[] | null;
  // False on indirect rows: last-activity is recorded on an identity's own
  // account and a resource-to-resource hop has none, so the Activity cell must
  // say "Not available" rather than "No activity data" (which reads as a
  // finding about the person).
  activityAvailable?: boolean;
}

// One identity on a grouped person row (PLO-5424). Unlike the flat row's
// `identity` (snake_case auth_type/mfa_enabled), the grouped endpoint sends
// camelCase throughout.
export interface MFPersonIdentity {
  id: string;
  extId?: string | null;
  displayName?: string | null;
  username?: string | null;
  type?: string | null;
  authType?: string | null;
  intId?: string | null;
  mfaEnabled?: boolean;
  lastAccessed?: string | null;
  accountId: string;
  isPrimary?: boolean;
  entitlementCount?: number;
}

// One row of the grouped (one-row-per-person) accounts listing (PLO-5424).
// A person with an SSO identity AND an email-password identity arrives as ONE
// row carrying every identity + the underlying flat account rows. Orphan/NHI
// rows come with `member: null` and `personKey` = the account id. Pagination
// is per person row. The primary identity is chosen server-side
// (integration-scanned first, then has-entitlements, then most recently
// active).
export interface MFGroupedPersonRow {
  type: "person";
  personKey: string;
  stage?: number;
  member: MFReviewAccount["member"] | null;
  primaryAccountId: string;
  primaryIdentityId: string | null;
  // Primary first.
  accountIds: string[];
  identities: MFPersonIdentity[];
  // Same shape as the flat listing's rows, one per identity.
  accounts: MFReviewAccount[];
  // Union across identities, deduped.
  entitlements?: MFAccessReviewAccountEntitlement[];
  entitlementCount?: number;
  flaggedCount?: number;
}

// Legacy access review API functions
const useListAppAccessReviews = (): MFAccessReviewWithTotals[] => {
  const data = useMemberGetQuery<MFAccessReviewWithTotals[]>(
    ["mf", "access_reviews"],
    "/members/access-reviews",
  );
  return data.data || [];
};

const useListAccessReviewAccounts = (
  filters: MFAccountAccessReviewFilters,
  limit: number = 20,
) => {
  return useMemberInfinitePostQuery<{}, MFAccessReviewAccount>(
    ["mf", "access_reviews", filters, "accounts"],
    "/members/access-reviews/accounts",
    filters,
    limit,
    {},
  );
};

export type MFReviewAccountsParams = {
  accessAccountIds: string[];
  accessReviewId: string;
  review?: MFAccessReviewAccount["review"];
  role?: string[];
  outOfScopeReason?: string;
};

const useReviewAccessReviewAccounts = () => {
  const { mutate, isLoading } = useMemberMutate(
    "POST",
    "/members/access-reviews/accounts/review",
    [["mf", "access_reviews"]],
  );
  return {
    isLoading,
    mutate: (body: MFReviewAccountsParams) =>
      mutate({
        access_review_account_ids: body.accessAccountIds,
        access_review_id: body.accessReviewId,
        review: body.review,
        role: body.role,
        out_of_scope_reason: body.outOfScopeReason,
      }),
  };
};

export type MFUpdateReviewAccount = {
  accountIds: string[];
  note: string;
};

const useUpdateAccessReviewAccount = () => {
  const { mutate, isLoading } = useMemberMutate(
    "POST",
    "/members/access-reviews/accounts/update",
    [["mf", "access_reviews"]],
  );
  return {
    isLoading,
    mutate: (body: MFUpdateReviewAccount) =>
      mutate({
        account_ids: body.accountIds,
        note: body.note,
      }),
  };
};

export type MFAddAccessReviewAccountParams = {
  accessReviewId: string;
  appId: string;
  memberId: string;
  role: string[];
};

const useAddAccessReviewAccount = () => {
  const { mutate, isLoading } = useMemberMutate(
    "POST",
    "/members/access-reviews/accounts/add",
    [["mf", "access_reviews"]],
  );
  return {
    isLoading,
    mutate: (body: MFAddAccessReviewAccountParams) =>
      mutate({
        access_review_id: body.accessReviewId,
        app_id: body.appId,
        member_id_to_add: body.memberId,
        role: body.role,
      }),
  };
};

// Campaign API functions
export type CampaignFilters = {
  status?: "in_progress" | "completed";
  search?: string;
  due_date?: {
    before?: string;
    after?: string;
  };
  sort_by?: "due_date" | "inserted_at";
  sort_direction?: "asc" | "desc";
  group_by?: "none" | "status";
};

const useListCampaigns = (filters?: CampaignFilters, options: any = {}) => {
  // Build flat query params
  const params = new URLSearchParams();
  if (filters) {
    if (filters.status) params.append("status", filters.status);
    if (filters.search) params.append("search", filters.search);
    if (filters.due_date?.before) params.append("due_date[before]", filters.due_date.before);
    if (filters.due_date?.after) params.append("due_date[after]", filters.due_date.after);
    if (filters.sort_by) params.append("sort_by", filters.sort_by);
    if (filters.sort_direction) params.append("sort_direction", filters.sort_direction);
    if (filters.group_by) params.append("group_by", filters.group_by);
  }

  const queryString = params.toString();
  const url = `/members/access-reviews/campaigns${queryString ? `?${queryString}` : ""}`;

  const { data, isLoading } = useMemberGetQuery<
    MFCampaign[] | { overdue: MFCampaign[]; in_progress: MFCampaign[]; complete: MFCampaign[] }
  >(
    ["mf", "campaigns", filters],
    url,
    {
      suspense: false,
      ...options,
    }
  );
  return { data: data || [], isLoading };
};

export type CampaignDetailsFilters = {
  sort_by?: "name" | "requires_review" | "completed";
  sort_direction?: "asc" | "desc";
  group_by?: "none" | "status";
};

const useGetCampaignDetails = (
  campaignId: string,
  filters?: CampaignDetailsFilters,
  options: any = {}
) => {
  // Build flat query params
  const params = new URLSearchParams();
  if (filters) {
    if (filters.sort_by) params.append("sort_by", filters.sort_by);
    if (filters.sort_direction) params.append("sort_direction", filters.sort_direction);
    if (filters.group_by) params.append("group_by", filters.group_by);
  }

  const queryString = params.toString();
  const url = `/members/access-reviews/campaigns/${campaignId}${queryString ? `?${queryString}` : ""}`;

  const { data, isLoading } = useMemberGetQuery<MFCampaignDetails>(
    ["mf", "campaigns", campaignId, "details", filters],
    url,
    {
      ...options,
      suspense: false,
    }
  );
  return { data: data, isLoading };
};

/** Reviewer-side progress for one campaign: how many of the member's resource
 *  reviews are done, out of how many. Shaped for `isComplete(done, total)`. */
export interface MFCampaignReviewerProgress {
  completed: number;
  total: number;
}

/**
 * Reviewer-side progress for a set of campaigns, keyed by campaign id
 * (PLO-5326). The campaigns LIST payload carries no per-member progress, so
 * this reads it from the per-campaign details endpoint.
 *
 * The query key deliberately mirrors `useGetCampaignDetails` with no filters —
 * the exact key each campaign card already uses (`useMemberGetQuery` appends
 * the access token, hence the trailing element) — so for a campaign whose card
 * is mounted this de-duplicates onto that card's existing query rather than
 * issuing a second request.
 *
 * It does still fetch for campaigns with no mounted card: the list renders one
 * tab at a time, and deciding which tab a campaign belongs to needs its
 * progress whether or not it is currently on screen. That is inherent to the
 * fix, not incidental — the alternative is the list payload carrying per-member
 * progress, which would be a server change.
 *
 * Campaigns whose details have not resolved yet are simply absent from the map.
 */
const useCampaignsReviewerProgress = (
  campaignIds: string[],
): Record<string, MFCampaignReviewerProgress> => {
  const accessToken = useMemberAuthStore((state) => state.accessToken);

  const results = useQueries({
    queries: campaignIds.map((id) => ({
      queryKey: ["mf", "campaigns", id, "details", undefined, accessToken],
      queryFn: async () =>
        membersApi.get<MFCampaignDetails>(
          `/members/access-reviews/campaigns/${id}`,
          accessToken as string,
        ),
      enabled: Boolean(accessToken),
      suspense: false,
      // The QueryClient defaults `useErrorBoundary: true` (App.tsx), which
      // would throw a failed fetch through the access-review subtree and
      // replace the whole Members route with the error screen. That is the
      // wrong trade here twice over: this hook fetches campaigns that have no
      // card on screen (the list renders one tab at a time), so an ordinary
      // 403 or 5xx on a campaign the member is not even looking at would blank
      // the page. Failing soft is already the designed behaviour — an absent
      // entry means "progress unknown", which buckets the campaign as Open.
      // Same opt-out, for the same reason, as the task detail queries.
      useErrorBoundary: false,
    })),
  });

  // `useQueries` returns a fresh array (and fresh result objects) every render,
  // so memoising on it directly would hand callers a new map each time and
  // defeat every downstream useMemo. `signature` is the memo KEY only — it
  // changes exactly when some campaign's resolved numbers change — while the
  // map itself is built from `results`. Reading `results` inside a memo keyed
  // on the signature is sound precisely because equal signatures imply equal
  // numbers, which is all the map holds.
  const entries = results.map((result, i) => {
    const payload = result.data;
    const stats = payload?.success ? payload.data?.stats : undefined;
    return stats
      ? ([campaignIds[i], { completed: stats.completed_reviews, total: stats.total_reviews }] as const)
      : null;
  });

  const signature = entries
    .map((e) => (e ? `${e[0]}:${e[1].completed}/${e[1].total}` : ""))
    .join("|");

  return useMemo(() => {
    const progress: Record<string, MFCampaignReviewerProgress> = {};
    for (const entry of entries) {
      if (entry) progress[entry[0]] = entry[1];
    }
    return progress;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);
};

const useListCampaignReviews = (
  campaignId: string,
  filters: Record<string, any> = {},
) => {
  const { data, isLoading } = useMemberPostQuery<MFCampaignReview[]>(
    ["mf", "campaigns", campaignId, "reviews", JSON.stringify(filters)],
    "/members/access-reviews/campaigns/:campaign_id/reviews",
    {
      campaign_id: campaignId,
      filters,
    },
  );
  return { data: data || [], isLoading };
};

const useGetReviewDetails = (reviewId: string) => {
  const { data, isLoading } = useMemberGetQuery<MFReviewDetails>(
    ["mf", "reviews", reviewId, "details"],
    `/members/access-reviews/reviews/${reviewId}`,
  );
  return { data, isLoading };
};

// AI Suggestions response types
type AISuggestionsData = {
  auto_approve: Array<{ account_ids: string[]; reasoning: string | null }>;
  auto_reject: Array<{ account_ids: string[]; reasoning: string | null }>;
  needs_review: Array<{ account_ids: string[]; reasoning: string | null }>;
};

type AISuggestionsResponse =
  | {
      success: true;
      status: "success";
      data: AISuggestionsData;
    }
  | {
      success: false;
      status: "failed";
      error: {
        message: string;
        failed_at: string;
      };
    }
  | {
      success: false;
      status: "pending";
      message: string;
    }
  | {
      status: 404;
      message: string;
    };

const useGetAISuggestions = (reviewId: string) => {
  // Use a direct query that doesn't filter by success
  const accessToken = useMemberAuthStore((state) => state.accessToken);
  const { data: rawResponse, isLoading, refetch, error } = useQuery(
    ["mf", "reviews", reviewId, "ai-suggestions", accessToken],
    async () => {
      const response = await fetch(
        `${API_BASE}members/access-reviews/ai-suggestions/${reviewId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "authorization": "Bearer " + accessToken,
            "x-org-subdomain": getSubdomain(),
          },
        }
      );

      const data = await response.json();

      // Don't throw on 404 or when we get a not_found error - these are expected states
      if (!response.ok && response.status !== 404 && response.status !== 400) {
        throw new Error(`API request failed: ${response.statusText}`);
      }

      // Return the data even if it's an error response, we'll handle it in the processing
      return data;
    },
    {
      enabled: !!reviewId && !!accessToken,
      retry: (failureCount, error: any) => {
        // Don't retry on 404 or failed status, only on network errors
        if (error?.response?.status === 404) return false;
        if (error?.message?.includes("failed")) return false;
        return failureCount < 3;
      },
      refetchInterval: (data) => {
        // Check the actual response status
        if (data?.status === "success") {
          return false; // Stop polling
        }
        if (data?.status === "failed") {
          return false; // Stop polling on failure
        }
        // Keep polling for pending or not found
        return 3000;
      }
    }
  );

  // Extract the actual data from the response
  let data: AISuggestionsData | null = null;
  let status: "success" | "failed" | "pending" | "not_found" = "pending";
  let errorMessage: string | null = null;

  if (rawResponse) {
    // Check various response formats
    if (rawResponse.error === "not_found") {
      // Handle the {error: "not_found"} response
      status = "not_found";
    } else if (rawResponse.status === "success" && rawResponse.success === true) {
      // We have successful data
      data = rawResponse.data;
      status = "success";
    } else if (rawResponse.status === "pending") {
      status = "pending";
    } else if (rawResponse.status === "failed") {
      status = "failed";
      errorMessage = rawResponse.error?.message || "AI generation failed";
    } else if (rawResponse.status === 404) {
      status = "not_found";
    }
  } else if (error) {
    // Handle error cases
    if (error?.message?.includes("404")) {
      status = "not_found";
    } else {
      status = "failed";
      errorMessage = error?.message || "Failed to fetch AI suggestions";
    }
  } else if (isLoading) {
    status = "pending";
  }

  return {
    data,
    isLoading,
    refetch,
    status,
    errorMessage,
    isGenerating: status === "pending" || status === "not_found"
  };
};

export const REVIEW_ACCOUNTS_PAGE_SIZE = 2000;

// Grouped (one-row-per-person) accounts listing (PLO-5424). Pagination is per
// person row.
// Decisions still post to the existing review endpoint with the row's
// accountIds (all of them = decide the whole person; a subset = per-identity).
// Keyed under ["mf", "reviews"] so every existing review mutation's
// invalidation refetches this too.
const useListReviewAccountsGrouped = (
  reviewId: string,
  filters: Record<string, any> = {},
  limit: number = 100,
) => {
  const query = useMemberInfinitePostQuery<
    Record<string, any>,
    MFGroupedPersonRow
  >(
    ["mf", "reviews", reviewId, "accounts-grouped", JSON.stringify(filters)],
    `/members/access-reviews/reviews/${reviewId}/accounts/grouped`,
    {
      review_id: reviewId,
      ...filters,
    },
    limit,
    {},
  );

  // Auto-drain every page: derived state (progress, stage partition, selection
  // expansion) needs the full person set, and in a multi-stage review
  // old-stage rows can fill a page and hide the current-stage ones.
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return query;
};

// The review's accounts as flat rows, unfolded from the grouped listing (each
// person row carries its accounts in the flat listing's exact shape). Sharing
// the grouped query means the review page fetches its accounts once, not once
// per shape: on a thousand-account review each fetch is megabytes of JSON, and
// every decision refetches it (PLO-6505). `undefined` until loaded.
const useListReviewAccountRows = (reviewId: string) => {
  const { data } = useListReviewAccountsGrouped(
    reviewId,
    {},
    REVIEW_ACCOUNTS_PAGE_SIZE,
  );
  return useMemo(
    () =>
      data?.pages.flatMap((page) => page.rows.flatMap((row) => row.accounts)),
    [data],
  );
};

export type MFCampaignReviewAccountsParams = {
  reviewId: string;
  accountIds: string[];
  review?: string;
  outOfScopeReason?: string;
  note?: string;
  role?: string[];
  markAsRemoved?: boolean;
  // Entitlement flagging - IDs from entitlements[].id (snapshot row IDs)
  flaggedSnapshotIds?: string[];
  flaggedNote?: string;
  // The subset of accountIds whose Luna recommendation this decision follows.
  // Computed by lunaAlignment; the server emits one "Luna recommendation
  // accepted" event per id, so callers with no suggestion data send nothing
  // and we default to [] rather than letting a missing key mean "all".
  lunaSuggestedAccountIds?: string[];
};

const useReviewCampaignAccounts = (reviewId: string) => {
  const { mutate, isLoading } = useMemberMutate(
    "POST",
    `/members/access-reviews/reviews/${reviewId}/review`,
    [
      ["mf", "campaigns"],
      ["mf", "reviews"],
    ],
  );
  return {
    isLoading,
    mutate: (params: MFCampaignReviewAccountsParams) =>
      mutate({
        review_id: params.reviewId,
        account_ids: params.accountIds,
        review: params.review,
        out_of_scope_reason: params.outOfScopeReason,
        note: params.note,
        role: params.role,
        mark_as_removed: params.markAsRemoved,
        flagged_snapshot_ids: params.flaggedSnapshotIds,
        flagged_note: params.flaggedNote,
        luna_suggested_account_ids: params.lunaSuggestedAccountIds ?? [],
      }),
  };
};

// Add account to campaign review
export type MFAddAccountToCampaignParams = {
  reviewId: string;
  accountId: string;
  // A combined review's add names the resource the account is on.
  resourceId?: string;
  entitlementIds?: string[];
  newEntitlementNames?: string[];
};

const useAddAccountToCampaignReview = () => {
  const { mutate, isLoading } = useMemberMutate(
    "POST",
    `/members/access-reviews/reviews/add-account`,
    [["mf", "campaigns"], ["mf", "reviews"], ["options"]],
  );

  return {
    isLoading,
    mutate: (params: MFAddAccountToCampaignParams) =>
      mutate({
        review_id: params.reviewId,
        account_id: params.accountId,
        resource_id: params.resourceId,
        entitlement_ids: params.entitlementIds,
        new_entitlement_names: params.newEntitlementNames,
      }),
  };
};

// Remove a review account whose access no longer exists at source (PLO-4803):
// marks the account already-removed and records the access as revoked, so it
// drops out of the review.
//
// Raw fetch (like useUpdateAccountEntitlements) rather than useMemberMutate: the
// caller drives the toast (a bulk run fans this out via Promise.allSettled, so a
// shared global error toast would fire once per failed account). Failures throw
// an Error carrying the server's human message so callers never surface
// "undefined"; business errors ride HTTP 200 with { success: false, error }.
const useRemoveNonExistentAccount = () => {
  const queryClient = useQueryClient();

  return {
    isLoading: false,
    mutate: async (accessReviewAccountId: string) => {
      const response = await fetch(
        `${API_BASE}members/access-reviews/accounts/remove-non-existent`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization:
              "Bearer " + useMemberAuthStore.getState().accessToken,
            "x-org-subdomain": getSubdomain(),
          },
          body: JSON.stringify({
            access_review_account_id: accessReviewAccountId,
          }),
        },
      );
      const body = await response.json().catch(() => ({}));
      if (!response.ok || body?.success === false) {
        throw new Error(memberErrorMessage(body, "Couldn't remove the account"));
      }
      queryClient.invalidateQueries(["mf", "campaigns"]);
      queryClient.invalidateQueries(["mf", "reviews"]);
      return body?.data ?? body;
    },
  };
};

// Raise a review-scope (account-less) general query to the campaign admin
// (PLO-4803). The `note` is the question. Raw fetch (like
// useRemoveNonExistentAccount) so the caller drives the toast + refetch: a
// business error rides HTTP 200 with { success: false, error } (house
// convention), thrown as an Error carrying the server's human message so the
// modal never surfaces "undefined".
const useRaiseGeneralQuery = (reviewId: string) => {
  const queryClient = useQueryClient();

  return {
    isLoading: false,
    mutate: async (note: string) => {
      const response = await fetch(
        `${API_BASE}members/access-reviews/reviews/${reviewId}/queries`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization:
              "Bearer " + useMemberAuthStore.getState().accessToken,
            "x-org-subdomain": getSubdomain(),
          },
          body: JSON.stringify({ note }),
        },
      );
      const body = await response.json().catch(() => ({}));
      if (!response.ok || body?.success === false) {
        throw new Error(memberErrorMessage(body, "Couldn't raise the query"));
      }
      queryClient.invalidateQueries(["mf", "campaigns"]);
      queryClient.invalidateQueries(["mf", "reviews"]);
      return body?.data ?? body;
    },
  };
};

// Entitlement option from the resource (for selection UI)
export interface MFEntitlementOption {
  id: string;
  name: string;
  description: string | null;
  type: string | null;
  resourceId: string;
  insertedAt: string;
  updatedAt: string;
}

// Get available entitlements for an account's resource
const useGetEntitlementOptions = (accountId: string | null) => {
  const { data, isLoading } = useMemberGetQuery<MFEntitlementOption[]>(
    ["mf", "access-reviews", "accounts", accountId, "entitlements", "options"],
    `/members/access-reviews/accounts/${accountId}/entitlements/options`,
    {
      enabled: !!accountId,
      suspense: false,
      useErrorBoundary: false,
    }
  );
  return { data: data || [], isLoading };
};

// Update entitlements for an account
export type MFUpdateAccountEntitlementsParams = {
  accountId: string;
  entitlementIds: string[];
  newEntitlementNames?: string[];
};

const useUpdateAccountEntitlements = () => {
  const queryClient = useQueryClient();
  const { isLoading } = useMemberMutate(
    "POST",
    "/members/access-reviews/accounts/entitlements",
    [["mf", "campaigns"], ["mf", "reviews"]],
  );

  return {
    isLoading,
    mutate: async (accountId: string, entitlementIds: string[], newEntitlementNames?: string[]) => {
      const body: Record<string, unknown> = { entitlement_ids: entitlementIds };
      if (newEntitlementNames && newEntitlementNames.length > 0) {
        body.new_entitlement_names = newEntitlementNames;
      }
      const response = await fetch(
        `${API_BASE}members/access-reviews/accounts/${accountId}/entitlements`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "authorization": "Bearer " + useMemberAuthStore.getState().accessToken,
            "x-org-subdomain": getSubdomain(),
          },
          body: JSON.stringify(body),
        }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to update entitlements");
      }
      const result = await response.json();
      queryClient.invalidateQueries(["mf", "campaigns"]);
      queryClient.invalidateQueries(["mf", "reviews"]);
      return result;
    },
  };
};

// Get available entitlements for a review's resource
// `resourceId` picks one of a combined review's resources; omitted, the
// review's own.
const useGetReviewEntitlements = (
  reviewId: string | null,
  resourceId?: string | null,
) => {
  const query = resourceId ? `?resource_id=${encodeURIComponent(resourceId)}` : "";
  const { data, isLoading } = useMemberGetQuery<MFEntitlementOption[]>(
    ["mf", "access-reviews", "reviews", reviewId, "entitlements", resourceId ?? null],
    `/members/access-reviews/reviews/${reviewId}/entitlements${query}`,
    {
      enabled: !!reviewId,
    }
  );
  return { data: data || [], isLoading };
};

// Bulk update entitlements for multiple accounts
const useBulkUpdateAccountEntitlements = () => {
  const updateEntitlements = useUpdateAccountEntitlements();
  const queryClient = useQueryClient();

  return {
    isLoading: updateEntitlements.isLoading,
    mutate: async (accountIds: string[], entitlementIds: string[], newEntitlementNames?: string[]) => {
      const results = await Promise.allSettled(
        accountIds.map((accountId) =>
          updateEntitlements.mutate(accountId, entitlementIds, newEntitlementNames)
        )
      );

      const failures = results.filter((r) => r.status === "rejected");
      if (failures.length > 0) {
        throw new Error(
          `Failed to update ${failures.length} of ${accountIds.length} accounts`
        );
      }

      // Invalidate queries to refresh data
      queryClient.invalidateQueries(["mf", "reviews"]);

      return results;
    },
  };
};

const useSubmitAccountSet = () => {
  const { mutate, isLoading } = useMemberMutate(
    "POST",
    "/members/access-reviews/account-sets/submit",
    [["mf", "campaigns"], ["mf", "reviews"], ["mf", "attestations"]],
  );
  return {
    isLoading,
    // `confirmedStatementIds` is only used in :reviewer_per_set mode — the
    // backend creates and signs an attestation row alongside submission. Other
    // modes ignore the field, so leaving it `undefined` is safe.
    mutate: (accountSetId: string, confirmedStatementIds?: string[]) =>
      mutate({
        account_set_id: accountSetId,
        confirmed_statement_ids: confirmedStatementIds,
      }),
  };
};

// Review-scoped batch submit (PLO-4803). The review page merges ALL of a
// member's account sets in a review into one list, so a single "Submit for
// approval" + one attestation must cover every submittable set the member
// owns in the review. This keys on `reviewId` (not a single account-set) and
// the backend submits + attests each of the member's sets in one call,
// recording the same `confirmedStatementIds` against each. Same
// `{ success: false, error }` 200-body convention as `useSubmitAccountSet` —
// a completeness gate failure on ANY set rejects the whole call.
const useSubmitReview = () => {
  const { mutate, isLoading } = useMemberMutate(
    "POST",
    "/members/access-reviews/reviews/submit",
    [["mf", "campaigns"], ["mf", "reviews"], ["mf", "attestations"]],
  );
  return {
    isLoading,
    mutate: (reviewId: string, confirmedStatementIds?: string[]) =>
      mutate({
        review_id: reviewId,
        confirmed_statement_ids: confirmedStatementIds,
      }),
  };
};

// Request a review deadline extension (PLO-4856). Same auth/conventions as the
// submit endpoint. `reason` is optional unless the cycle's rules require it.
export type MFRequestExtensionParams = {
  accountSetId: string;
  requestedDays: number;
  reason?: string;
};

// Response shape from the request-extension endpoint. `status` is
// `"auto_granted"` when the request was under the auto-grant threshold and
// applied immediately (a new `dueDateOverride`/`effectiveDueDate` follows on
// refetch), or `"pending"` when it queued for an admin decision.
export type MFExtensionRequestResult = {
  id: string;
  status: "auto_granted" | "pending";
  requestedDays: number;
  reason: string | null;
  decidedDays: number | null;
  decisionReason: string | null;
  accountSetId: string;
  insertedAt: string;
};

const useRequestDeadlineExtension = () => {
  // Invalidate reviews (refetches the review-details payload, so the new
  // `effectiveDueDate`/`pendingRequest` surface) and campaigns (the campaign
  // due-date rollup). Mirrors `useSubmitAccountSet`'s invalidation set.
  const { mutate, isLoading } = useMemberMutate<MFExtensionRequestResult>(
    "POST",
    "/members/access-reviews/account-sets/request-extension",
    [["mf", "campaigns"], ["mf", "reviews"]],
  );
  return {
    isLoading,
    mutate: (body: MFRequestExtensionParams) =>
      mutate({
        account_set_id: body.accountSetId,
        requested_days: body.requestedDays,
        reason: body.reason,
      }),
  };
};

// Attestation types (Phase 5/6 backend)
export type MFAttestationStatus =
  | "pending"
  | "attested"
  | "declined"
  | "superseded"
  | "skipped_no_assignee";

export type MFAttestationMode =
  | "configured_per_review"
  | "configured_per_set"
  | "reviewer_per_set";

export interface MFAttestationStatement {
  id: string;
  text: string;
  required: boolean;
  order: number;
  confirmed: boolean;
  confirmedAt: string | null;
}

// Owner sign-off reviews (PLO-5185) have no account rows: the owner's
// signature is the whole review. Entitlement-only reviews (PLO-5731) go the
// other way: every row is ONE entitlement of one account, so a person holding
// three roles is three rows, each decided on its own. Older server responses
// omit the field.
export type MFReviewType =
  | "standard"
  | "attestation_only"
  | "entitlement_only";

export interface MFAttestationSummary {
  id: string;
  status: MFAttestationStatus;
  mode: MFAttestationMode;
  accessReviewId: string;
  accessReviewAccountSetId: string | null;
  resourceName: string | null;
  // The attested resource's type (e.g. "app", "group") and image, so the
  // attestation page can headline the system the way the campaign side panel
  // does. Older server responses omit both: render the generic glyph then.
  resourceType?: string | null;
  resourceImageUrl?: string | null;
  cycleName: string | null;
  insertedAt: string;
  reviewType?: MFReviewType;
}

export interface MFAttestation extends MFAttestationSummary {
  statements: MFAttestationStatement[];
  attestedAt: string | null;
  declinedAt: string | null;
  declineReason: string | null;
  // Read-only per-review summary (counts) the portal can show as context
  reviewSummary?: {
    totalAccounts: number;
    appropriate: number;
    notRequired: number;
    outOfScope: number;
    removed: number;
  };
}

const useListMyAttestations = () => {
  const { data, isLoading } = useMemberGetQuery<MFAttestationSummary[]>(
    ["mf", "attestations"],
    "/members/access-reviews/attestations",
    { suspense: false },
  );
  return { data: data || [], isLoading };
};

const useGetAttestation = (attestationId: string | null) => {
  const { data, isLoading } = useMemberGetQuery<MFAttestation>(
    ["mf", "attestations", attestationId],
    `/members/access-reviews/attestations/${attestationId}`,
    {
      enabled: !!attestationId,
      suspense: false,
    },
  );
  return { data, isLoading };
};

const useSignAttestation = (attestationId: string) => {
  const { mutate, isLoading } = useMemberMutate(
    "POST",
    `/members/access-reviews/attestations/${attestationId}/sign`,
    [["mf", "attestations"], ["mf", "attestations", attestationId]],
  );
  return {
    isLoading,
    mutate: (confirmedStatementIds: string[]) =>
      mutate({ confirmed_statement_ids: confirmedStatementIds }),
  };
};

const useDeclineAttestation = (attestationId: string) => {
  const { mutate, isLoading } = useMemberMutate(
    "POST",
    `/members/access-reviews/attestations/${attestationId}/decline`,
    [["mf", "attestations"], ["mf", "attestations", attestationId]],
  );
  return {
    isLoading,
    mutate: (declineReason: string) =>
      mutate({ decline_reason: declineReason }),
  };
};

export const MF_ACCESS_REVIEWS = {
  // Legacy functions
  useListAccessReviewAccounts,
  useListAppAccessReviews,
  useReviewAccessReviewAccounts,
  useAddAccessReviewAccount,
  useUpdateAccessReviewAccount,

  // Campaign functions
  useListCampaigns,
  useGetCampaignDetails,
  useCampaignsReviewerProgress,
  useListCampaignReviews,
  useGetReviewDetails,
  useGetAISuggestions,
  useListReviewAccountRows,
  useListReviewAccountsGrouped,
  useReviewCampaignAccounts,
  useAddAccountToCampaignReview,
  useRemoveNonExistentAccount,
  useRaiseGeneralQuery,

  // Entitlement management
  useGetEntitlementOptions,
  useGetReviewEntitlements,
  useUpdateAccountEntitlements,
  useBulkUpdateAccountEntitlements,

  // Account set approval
  useSubmitAccountSet,
  useSubmitReview,

  // Deadline extensions (PLO-4856)
  useRequestDeadlineExtension,

  // Attestations
  useListMyAttestations,
  useGetAttestation,
  useSignAttestation,
  useDeclineAttestation,
};
